import React, { FC } from 'react';
import styled from 'styled-components';
import { DayPicker } from 'components/molecules/DayPicker';
import { DayPickerProps, Mode } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/common/styled/common-styled';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button';
import { Popover } from 'components/atoms/Popover';
import { PopoverContentProps } from '@radix-ui/react-popover';

type DayPickerPropsWithMode = DayPickerProps & {
  mode: Mode;
  isLoading?: boolean;
};

type FooterProps = {
  onClearButtonClick?: React.MouseEventHandler;
  onCancelButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onApplyButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  isClearButtonDisabled?: boolean;
  isApplyButtonDisabled?: boolean;
  isCancelButtonDisabled?: boolean;
  onApplyButtonKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};

type PopoverProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  asChild?: boolean;
  trigger?: React.ReactNode;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  sideOffset?: PopoverContentProps['sideOffset'];
  alignOffset?: PopoverContentProps['alignOffset'];
  portalled?: boolean;
};

type DayPickerPopoverType = FC<
  DayPickerPropsWithMode & FooterProps & PopoverProps
>;

const DayPickerPopover: DayPickerPopoverType = ({
  open,
  onOpenChange,
  asChild,
  trigger,
  align = 'start',
  side = 'bottom',
  sideOffset,
  alignOffset,
  portalled = true,
  onClearButtonClick,
  isClearButtonDisabled,
  onCancelButtonClick,
  isCancelButtonDisabled,
  onApplyButtonClick,
  isApplyButtonDisabled,
  onApplyButtonKeyDown,
  ...props
}) => {
  const { t: translate } = useTranslation();

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      asChild={asChild}
      align={align}
      side={side}
      portalled={portalled}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
    >
      <DayPicker
        footer={
          <StyledFooter
            direction="row"
            justifyContent="space-between"
            gap={12}
            role="group"
            width="100%"
          >
            {!!onClearButtonClick && (
              <ClearButton
                className="date-range-calendar-dropdown-clear-picker-button"
                onClick={onClearButtonClick}
                disabled={isClearButtonDisabled}
                tabIndex={0}
                aria-label={translate('clear')}
              >
                {translate('clear')}
              </ClearButton>
            )}
            <Flex
              direction="row"
              width="100%"
              justifyContent="flex-end"
              gap={12}
              role="group"
            >
              <SecondaryButton
                className="date-range-calendar-cancel-picker-button"
                onClick={onCancelButtonClick}
                disabled={isCancelButtonDisabled}
                tabIndex={0}
                aria-label={translate('cancel')}
              >
                {translate('cancel')}
              </SecondaryButton>
              <PrimaryButton
                className="calendar-apply-button"
                tabIndex={0}
                onClick={onApplyButtonClick}
                disabled={isApplyButtonDisabled}
                onKeyDown={(e) => onApplyButtonKeyDown?.(e)}
                aria-label={translate('apply')}
              >
                {translate('apply')}
              </PrimaryButton>
            </Flex>
          </StyledFooter>
        }
        {...props}
      />
    </Popover>
  );
};

const StyledFooter = styled(Flex)`
  margin-top: 16px;
`;

const ClearButton = styled(SecondaryButton)`
  border: none;
  text-decoration: underline;

  &:hover {
    border: none;
  }
`;

export default DayPickerPopover;
